import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Plus } from "lucide-react";
import { MaskLines, EASE } from "./motion";
import { useCart } from "../context/CartContext";
import { gbp, REVIEW_COUNT, REVIEW_RATING } from "../lib/shop";
import { toast } from "sonner";

const HERO_IMG =
  "https://cdn.instant.so/sites/BVE3NozdcMrChcgt/assets/x5AO2aRUAo0fvkjS/img0503.jpg?width=1600";

export default function Hero({ product }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const { add } = useCart();

  const quickAdd = () => {
    if (!product) return;
    const v = product.variants.find((x) => x.available);
    if (!v) return;
    add({
      variantId: v.id,
      handle: product.handle,
      title: product.title,
      colour: v.colour,
      size: v.size,
      price: product.price,
      image: product.images[0],
      preorder: product.preorder,
      sellingPlanId: product.sellingPlanId || null,
    });
    toast.success(`Added — ${product.title} (${v.colour} / ${v.size})`);
  };

  return (
    <section ref={ref} className="relative min-h-[88svh] md:min-h-[100svh] flex items-end md:items-center overflow-hidden">
      {/* backdrop image with parallax */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform" data-testid="hero-parallax-image">
        <img
          src={HERO_IMG}
          alt="01 London — premium minimal streetwear"
          className="w-full h-full object-cover object-[62%_center] opacity-70"
          onError={(e) => {
            if (product) e.currentTarget.src = product.images[2] || product.images[0];
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1500px] mx-auto w-full px-5 md:px-10 pt-36 pb-28 md:py-44">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8, ease: EASE }}
          className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-ash uppercase mb-6 md:mb-8"
        >
          Premium minimal streetwear — Est. London 2025
        </motion.p>

        <MaskLines
          lines={[<>START WITH</>, <>WHAT <br className="lg:hidden" />MATTERS.</>]}
          className="font-display font-extrabold uppercase tracking-tighter leading-[0.92] text-[11vw] sm:text-[9vw] lg:text-[8.2vw] pr-2"
          delay={0.35}
          testId="hero-headline"
        />

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-6 md:mt-8 max-w-md text-ash text-sm md:text-base leading-relaxed"
        >
          Premium essentials for those who choose substance over hype. 350 GSM heavyweight hoodies, cut oversized, made in London.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to={product ? `/product/${product.handle}` : "/product/01-icon-oversized-hoodie"}
            className="group inline-flex items-center gap-3 bg-bone text-ink font-mono text-xs tracking-[0.2em] px-8 py-4 hover:bg-ember hover:text-white transition-colors duration-400"
            data-testid="hero-cta-button"
          >
            SHOP THE ICON HOODIE
            <ArrowRight className="w-4 h-4 transition-transform duration-400 group-hover:translate-x-1.5" />
          </Link>
          <Link
            to="/shop"
            className="font-mono text-xs tracking-[0.2em] text-ash hover:text-bone border-b border-white/20 hover:border-bone pb-1 transition-colors duration-300"
            data-testid="hero-shop-all-link"
          >
            SHOP ALL
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-10 flex items-center gap-3 font-mono text-[10px] md:text-[11px] tracking-[0.16em] text-ash uppercase"
          data-testid="hero-review-chip"
        >
          <span className="flex text-amber-400">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-current" />
            ))}
          </span>
          <span>{REVIEW_RATING} — {REVIEW_COUNT} reviews</span>
          <span className="w-1 h-1 rotate-45 bg-ash/60" />
          <span>Free UK delivery over £75</span>
        </motion.div>
      </motion.div>

      {/* floating quick-add card */}
      {product && (
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 1, ease: EASE }}
          className="hidden lg:flex absolute bottom-10 right-10 z-10 items-stretch bg-coal/90 backdrop-blur-xl border border-white/10 w-[340px]"
          data-testid="hero-quick-add-card"
        >
          <div className="w-24 shrink-0 overflow-hidden">
            <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 p-4">
            <p className="font-mono text-[9px] tracking-[0.2em] text-ember uppercase" data-testid="hero-stock-meter">
              {product.badge || product.tag}
            </p>
            <p className="font-display font-bold text-sm uppercase tracking-tight mt-1.5 leading-tight">{product.title}</p>
            <div className="flex items-center justify-between mt-2.5">
              <span className="font-mono text-sm">{gbp(product.price)}</span>
              <button
                onClick={quickAdd}
                className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] bg-bone text-ink px-3 py-2 hover:bg-ember hover:text-white transition-colors duration-300"
                data-testid="hero-quick-add-button"
              >
                <Plus className="w-3.5 h-3.5" /> QUICK ADD
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
}
